import React from 'react';

import { classNames, isEmpty, isNumber, isArray, isFunction, isNumeric } from 'util/utils';

import closeSvg from 'assets/svg/close.svg';
import plusSvg from 'assets/svg/plus.svg';
import './input.less';

function Buttons({ onAdd, onRemove }) {
  return (
    (isFunction(onAdd) || isFunction(onRemove)) && (
      <div className="plusInputRemove">
        {isFunction(onAdd) && (
          <div className="plusInputItem">
            <img src={plusSvg} alt="" className="plusInput" />
          </div>
        )}
        {isFunction(onRemove) && (
          <div className="closeInoutItem">
            <img src={closeSvg} alt="" className="closeInput" />
          </div>
        )}
      </div>
    )
  );
}

export const InputField = React.memo(({ label, required, error, onAdd, onRemove, ...rest }) => {
  return (
    <div className={`from-group inputBlock ${rest?.classNameHead}`}>
      <label>
        {label} {required && <span className="required-ast">*</span>}
      </label>
      <div className={classNames(['inputWrapper', Boolean(error) && 'error-message', rest?.className])}>
        <input {...rest} />
        <Buttons {...{ onAdd, onRemove }} />
        {Boolean(error) && <div className="error">{error}</div>}
      </div>
    </div>
  );
});
InputField.displayName = 'InputField';

const InputBlock = InputField;
export default InputBlock;

export const InputText = React.memo((props) => {
  const { className, label, required, error, ...rest } = props;

  return (
    <div className={classNames([`from-group inputBlock`, className])}>
      <label>
        {label} {required && <span className="required-ast">*</span>}
      </label>
      <div className={classNames(['inputWrapper', Boolean(error) && 'error-message', rest?.className])}>
        <input {...rest} />
        {Boolean(error) && <div className="error">{error}</div>}
      </div>
    </div>
  );
});
InputText.displayName = 'InputText';

export const InputNumber = React.memo((props) => {
  const {
    label,
    required,
    error,
    onChange,
    onBlur,
    onAdd,
    className,
    onRemove,
    min,
    max,
    showLimitErrors,
    ...rest
  } = props;

  const [_error, _setError] = React.useState(undefined);

  const handleChange = React.useCallback(
    (event, castToNumber) => {
      if (!isEmpty(_error)) _setError(undefined);
      event.preventDefault();
      const value = event?.target?.value;
      event.target.value = '';

      if (!isNumeric(value)) return; // return if not numeric fragment
      const number = Number(value); // cast to number
      const valueIsNumber = isNumber(number);

      if (!isEmpty(value) && valueIsNumber) {
        // check limits if not empty and type is number
        if (isNumber(min) && number < min) {
          if (showLimitErrors) _setError(`${label} can't be less than ${min}`);
          return;
        }
        if (isNumber(max) && number > max) {
          if (showLimitErrors) _setError(`${label} can't be more than ${max}`);
          return;
        }
      }
      event.target.value = value;

      if (castToNumber) {
        return onChange(valueIsNumber && !isEmpty(value) ? number : null);
      }

      onChange(value);
    },
    [_error, onChange, min, max, showLimitErrors, label],
  );

  const handleBlur = React.useCallback(
    (event) => {
      handleChange(event, true); // cast to number on blur
      onBlur && onBlur(event);
    },
    [handleChange, onBlur],
  );

  const value = React.useMemo(() => (isEmpty(rest?.value) ? '' : rest?.value), [rest?.value]);

  const errMessage = React.useMemo(() => _error ?? error, [_error, error]);

  return (
    <div className={`from-group inputBlock ${className}`}>
      <label>
        {label} {required && <span className="required-ast">*</span>}
      </label>
      <div className={classNames(['inputWrapper', errMessage && 'error-message', rest?.className])}>
        <input {...rest} value={value} onChange={handleChange} onBlur={handleBlur} />
        <Buttons {...{ onAdd, onRemove }} />
        {errMessage && <div className="error">{errMessage}</div>}
      </div>
    </div>
  );
});
InputNumber.displayName = 'InputNumber';

export function InputPercentage(props) {
  return <InputNumber min={-100} max={100} {...props} />;
}

export const InputFloat = React.memo((props) => {
  const { label, required, error, ...rest } = props;

  return (
    <div className="from-group inputBlock">
      <label>
        {label} {required && <span className="required-ast">*</span>}
      </label>
      <div className={classNames(['inputWrapper', Boolean(error) && 'error-message', rest?.className])}>
        <input
          onKeyPress={(evt) => {
            if ((evt.which < 48 || evt.which > 57) && evt.which != 46) {
              evt.preventDefault();
            }
          }}
          {...rest}
          // min="0.00"
        />
        {Boolean(error) && <div className="error">{error}</div>}
      </div>
    </div>
  );
});
InputFloat.displayName = 'InputFloat';

export function PhoneInputField({ label, error, onAdd, onRemove, onChange, value, ...rest }) {
  const [comopositeValue, setCompositeValue] = React.useState(isArray(value) ? value : ['', '', '']);

  const handleChange = (index, newValue) =>
    setCompositeValue((compositeValue) => {
      compositeValue[index] = newValue;
      onChange([...compositeValue]);
      return [...compositeValue];
    });

  return (
    <div className="from-group inputBlock">
      {label && <label>{label}</label>}
      <div className={classNames(['inputWrapper', Boolean(error) && 'error-message', 'mobileFiled'])}>
        <input
          className="smallInput"
          placeholder="ISD"
          value={comopositeValue[0]}
          onChange={(e) => handleChange(0, e.target.value)}
        />
        <input
          className="smallInput"
          placeholder="STD"
          value={comopositeValue[1]}
          onChange={(e) => handleChange(1, e.target.value)}
        />
        <input {...rest} value={comopositeValue[2]} onChange={(e) => handleChange(2, e.target.value)} />
        <Buttons {...{ onAdd, onRemove }} />
        {Boolean(error) && <div className="error">{error}</div>}
      </div>
    </div>
  );
}
